import { useEffect } from 'react';

function isUnresolvedToken(value) {
  return typeof value === 'string' && /#\{.+\}#/.test(value);
}

function toAbsoluteURL(url) {
  if (!url || isUnresolvedToken(url)) {
    return null;
  }

  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return null;
  }
}

export function useAnalyticsScript(src, { onLoad, onError } = {}) {
  useEffect(() => {
    const absoluteSrc = toAbsoluteURL(src);

    if (!absoluteSrc) {
      return;
    }

    const existingScript = [...document.scripts].find(
      (script) => script.src === absoluteSrc,
    );

    if (existingScript) {
      if (typeof onLoad === 'function') {
        if (
          existingScript.dataset.loaded === 'true' ||
          existingScript.readyState === 'complete'
        ) {
          onLoad();
        } else {
          existingScript.addEventListener('load', onLoad, { once: true });
        }
      }

      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = absoluteSrc;
    script.async = true;

    const handleLoad = () => {
      script.dataset.loaded = 'true';
      onLoad?.();
    };

    const handleError = () => {
      onError?.(new Error(`Failed to load analytics script: ${absoluteSrc}`));
    };

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
  }, [src, onLoad, onError]);
}
